/**
 * Cart inquiry form (sections/cart-inquiry.liquid).
 *
 * Liquid renders the cart summary at page load. When a shopper changes a quantity
 * Dawn re-renders the cart sections via the Section Rendering API, but this section
 * is not in that section list, so its hidden field can go stale. Before submitting we
 * refetch /cart.js and rebuild the hidden field so the email always matches the
 * live cart. If the fetch fails we fall back to the server-rendered value rather
 * than blocking the shopper.
 */
class CartInquiry extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('.cart-inquiry__toggle');
    this.panel = this.querySelector('.cart-inquiry__panel');
    this.form = this.querySelector('form');
    this.cartField = this.querySelector('#CartInquiryForm-cart');
    this.honeypot = this.querySelector('#CartInquiryForm-website');
    this.preview = this.querySelector('[data-cart-inquiry-preview]');
    this.maxLineItems = parseInt(this.dataset.maxLineItems, 10) || 50;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    if (this.toggle && this.panel) {
      this.toggle.addEventListener('click', this.onToggle.bind(this));
    }

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // The Liquid form block emits a marker when it rendered a success or error
    // message (form.posted_successfully? is only in scope inside that block).
    // Open the panel so the message is visible rather than collapsed.
    const state = this.querySelector('[data-cart-inquiry-state]');
    if (state && this.panel) {
      this.panel.hidden = false;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');

      if (state.dataset.cartInquiryState === 'posted') {
        this.classList.add('is-posted');
      }

      // Shopify redirects to #CartInquiryForm, so the browser handles the scroll
      // itself. scroll-margin-top in custom-cart-inquiry.css keeps the message clear
      // of the sticky header; nudge it into view here only if the hash is absent.
      const message = this.querySelector('.form-status');
      if (message && !window.location.hash) {
        message.scrollIntoView({ block: 'center' });
      }
    }

    // Dawn's cart.js re-renders a hardcoded list of sections (main-cart-items,
    // main-cart-footer, the icon bubble, the live region) and this section is not
    // among them. Subscribing to cart-update keeps the visible preview in sync when
    // the panel is already open, and hides the whole section when the cart empties.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cart = event && event.cartData;
        if (!cart || typeof cart.item_count === 'undefined') return;

        this.toggleEmptyState(cart.item_count === 0);

        if (cart.items) {
          if (this.cartField) this.cartField.value = this.buildSummary(cart);
          this.renderPreview(cart);
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  toggleEmptyState(isEmpty) {
    // Never hide a just-submitted success message, even if the cart is now empty.
    if (this.classList.contains('is-posted')) return;

    this.classList.toggle('is-empty', isEmpty);

    // Collapse the panel if the cart empties while it is open, so a shopper can't
    // submit an inquiry for a cart that no longer has anything in it.
    if (isEmpty && this.toggle && this.panel) {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
    }
  }

  onToggle() {
    const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';

    this.toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    this.panel.hidden = isOpen;

    if (!isOpen) {
      // Refresh the preview so shoppers see what will actually be sent.
      this.refreshFromCart();
      const firstField = this.panel.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
      if (firstField) firstField.focus({ preventScroll: true });
    }
  }

  onSubmit(event) {
    // Bots fill hidden fields; real shoppers never see this one.
    if (this.honeypot && this.honeypot.value !== '') {
      event.preventDefault();
      return;
    }

    // Let the browser's own required/email validation run and stop the submit
    // first — form.submit() below bypasses it entirely.
    if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) {
      event.preventDefault();
      return;
    }

    // Guards against a second submit landing while the refresh is in flight
    // (e.g. Enter pressed repeatedly). form.submit() below does not re-fire
    // this handler, so the refresh runs exactly once.
    if (this.submitting) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.submitting = true;
    this.setSubmitBusy(true);

    this.refreshFromCart()
      .catch(() => {
        // Keep the Liquid-rendered fallback value already in the field.
      })
      .finally(() => {
        // form.submit() skips validation, which we already ran above.
        this.form.submit();
      });
  }

  setSubmitBusy(isBusy) {
    const button = this.form.querySelector('button[type="submit"]');
    if (!button) return;

    // Deliberately not Dawn's `loading` class: it sets `color: transparent` and
    // expects a `.loading__spinner` child, which this button does not have —
    // the label would just disappear. `disabled` alone prevents double-submits.
    button.disabled = isBusy;
    button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  refreshFromCart() {
    // window.routes is defined in layout/theme.liquid, the same source Dawn's
    // cart.js uses. Fall back to the default path if it is ever missing.
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        if (this.cartField) this.cartField.value = this.buildSummary(cart);
        this.renderPreview(cart);
        return cart;
      });
  }

  buildSummary(cart) {
    const items = cart.items.slice(0, this.maxLineItems);
    const lines = items.map((item, index) => {
      const parts = [`${index + 1}. ${item.product_title}`];

      if (item.variant_title && !item.variant_title.includes('Default')) {
        parts.push(`   Variant: ${item.variant_title}`);
      }
      if (item.sku) parts.push(`   SKU: ${item.sku}`);

      parts.push(`   Quantity: ${item.quantity}`);
      parts.push(`   Price: ${this.formatMoney(item.final_line_price)}`);

      return parts.join('\n');
    });

    const overflow = cart.items.length - items.length;
    if (overflow > 0) {
      lines.push(`... and ${overflow} more item${overflow > 1 ? 's' : ''} not listed above.`);
    }

    lines.push(`\nCart total: ${this.formatMoney(cart.total_price)}`);
    lines.push(`Total items: ${cart.item_count}`);

    return lines.join('\n');
  }

  renderPreview(cart) {
    if (!this.preview) return;

    const items = cart.items.slice(0, this.maxLineItems);
    const overflow = cart.items.length - items.length;

    const rows = items.map((item) => {
      const li = document.createElement('li');
      li.className = 'cart-inquiry__summary-item';

      const title = document.createElement('span');
      title.className = 'cart-inquiry__summary-title';
      title.textContent =
        item.variant_title && !item.variant_title.includes('Default')
          ? `${item.product_title} — ${item.variant_title}`
          : item.product_title;

      const qty = document.createElement('span');
      qty.className = 'cart-inquiry__summary-qty';
      qty.textContent = `× ${item.quantity}`;

      li.append(title, qty);
      return li;
    });

    if (overflow > 0) {
      const more = document.createElement('li');
      more.className = 'cart-inquiry__summary-item cart-inquiry__summary-item--more';
      const moreTemplate =
        (overflow > 1 ? window.cartStrings?.inquiryMoreItems : window.cartStrings?.inquiryMoreItem) ||
        `… and [count] more item${overflow > 1 ? 's' : ''}`;
      more.textContent = moreTemplate.replace('[count]', overflow);
      rows.push(more);
    }

    this.preview.replaceChildren(...rows);
  }

  /**
   * Mirrors Shopify's money filter for the common format strings. Falls back to a
   * plain decimal if the shop uses a format this doesn't recognise — the amount is
   * still correct, only the currency decoration differs.
   */
  formatMoney(cents) {
    const format = this.moneyFormat;

    // Thousands separators applied to the integer part only.
    const group = (intPart, separator) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

    const build = (decimals, thousands, decimalMark) => {
      const fixed = (cents / 100).toFixed(decimals);
      const [intPart, decPart] = fixed.split('.');
      const grouped = group(intPart, thousands);
      return decPart ? `${grouped}${decimalMark}${decPart}` : grouped;
    };

    // Longest placeholders first: '{{amount_with_comma_separator}}' contains no
    // other placeholder as a substring, but ordering by specificity is safer if
    // Shopify adds formats later.
    const replacements = [
      ['{{amount_no_decimals_with_comma_separator}}', () => build(0, '.', ',')],
      ['{{amount_no_decimals_with_space_separator}}', () => build(0, ' ', ',')],
      ['{{amount_with_comma_separator}}', () => build(2, '.', ',')],
      ['{{amount_with_space_separator}}', () => build(2, ' ', ',')],
      ['{{amount_no_decimals}}', () => build(0, ',', '.')],
      ['{{amount}}', () => build(2, ',', '.')],
    ];

    for (const [placeholder, valueFn] of replacements) {
      if (format.includes(placeholder)) {
        return format.replace(placeholder, valueFn());
      }
    }

    // Unrecognised format — the amount is still correct, only the decoration differs.
    return build(2, ',', '.');
  }
}

if (!customElements.get('cart-inquiry')) {
  customElements.define('cart-inquiry', CartInquiry);
}
